// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: how the mail list and the reader are laid out, and which reply the reader leads
// with, set in Settings > Mail.

import {makePersistent} from '@app/features/platform/utils/MobXPersistence';
import {makeAutoObservable} from 'mobx';

/** Comfortable: roomy three lines. Compact: tighter three lines. Dense: sender and subject only. */
export type MailListDensity = 'comfortable' | 'compact' | 'dense';
export const MAIL_LIST_DENSITIES: ReadonlyArray<MailListDensity> = ['comfortable', 'compact', 'dense'];

/** The reader's main reply button, and how Konvo's drafted replies go out. */
export type MailDefaultReply = 'reply_all' | 'reply';
export const MAIL_DEFAULT_REPLIES: ReadonlyArray<MailDefaultReply> = ['reply_all', 'reply'];

class MailListPreferences {
	densityValue: MailListDensity = 'compact';
	/** One row per conversation, newest mail on top, with how many it holds (as Outlook does). */
	groupConversations = true;
	/** The reader folds the earlier mail of the conversation (your replies too) above the open one. */
	readAsConversation = true;
	/** Reply all unless changed: most mail here is answered to everyone on it. */
	defaultReplyValue: MailDefaultReply = 'reply_all';

	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
		void makePersistent(
			this,
			'MailListPreferences',
			['densityValue', 'groupConversations', 'readAsConversation', 'defaultReplyValue'],
			{syncAcrossTabs: true},
		);
	}

	get defaultReply(): MailDefaultReply {
		return MAIL_DEFAULT_REPLIES.includes(this.defaultReplyValue) ? this.defaultReplyValue : 'reply_all';
	}

	setDefaultReply(reply: MailDefaultReply): void {
		this.defaultReplyValue = reply;
	}

	get density(): MailListDensity {
		return MAIL_LIST_DENSITIES.includes(this.densityValue) ? this.densityValue : 'compact';
	}

	setDensity(density: MailListDensity): void {
		this.densityValue = density;
	}

	setGroupConversations(on: boolean): void {
		this.groupConversations = on;
	}

	setReadAsConversation(on: boolean): void {
		this.readAsConversation = on;
	}
}

export default new MailListPreferences();
